/**
 * 本地后端配置解析（前后端共享逻辑）
 *
 * 普通 pnpm dev 沿用 .env.development 的 VITE_USE_LOCAL_BACKEND / VITE_LOCAL_BACKEND_URL。
 * 本地后端切换器插件启用时，浮层 UI 把用户选择的本地后端写入
 * localStorage['__local_backend_override__']，并由该值临时覆盖 drafting 后端。
 *
 * 注意：qa/stage 环境选择不再写 localStorage，而是通过 Vite dev-only 接口修改
 * .env.development 的 devEnv，使 passport/account/drafting 等整套 env 保持一致。
 *
 * 本模块统一解析，供 base-url.ts（算 DRAFTING_URI）和 enable-token-base.ts
 * （决定是否注入本地后端专用 JWT header）共用，避免逻辑分叉。
 *
 * 仅在 import.meta.env.DEV 下生效；生产构建里 readOverride 永远返回 null。
 */

export const LOCAL_BACKEND_OVERRIDE_KEY = '__local_backend_override__';

export interface LocalOverride {
    kind: 'local';
    /** 本地后端裸 URL，如 http://localhost:8550 */
    url: string;
}

export type BackendOverride = LocalOverride;

declare const __DRAFTING_LOCAL_BACKEND_SWITCHER_ENABLED__: boolean | undefined;

type LocalBackendEnv = {
    DEV?: boolean;
    VITE_USE_LOCAL_BACKEND?: string;
    VITE_LOCAL_BACKEND_URL?: string;
};

interface OverrideSources {
    isDev: boolean;
    localBackendSwitcherEnabled: boolean;
    storage?: Pick<Storage, 'getItem'>;
    env: LocalBackendEnv;
}

function readStorageOverride(storage?: Pick<Storage, 'getItem'>): string | null {
    let raw: string | null = null;
    try {
        raw = storage?.getItem(LOCAL_BACKEND_OVERRIDE_KEY) || null;
    } catch {
        return null; // localStorage 不可用
    }
    if (!raw || raw[0] === '{') {
        return null;
    }
    return raw;
}

function readEnvOverride(env: LocalBackendEnv): string | null {
    if (env.VITE_USE_LOCAL_BACKEND !== '1') {
        return null;
    }
    return env.VITE_LOCAL_BACKEND_URL || null;
}

/**
 * 根据运行来源解析本地后端覆盖。
 *
 * @param sources.isDev 是否为 Vite dev 模式；生产构建始终返回 null。
 * @param sources.localBackendSwitcherEnabled 本地后端切换器插件是否启用。
 * @param sources.storage 浏览器存储来源，仅插件启用时读取。
 * @param sources.env Vite 环境变量来源，仅普通 pnpm dev fallback 时读取。
 * @returns 本地后端覆盖；没有覆盖或不可用时返回 null。
 */
export function readOverrideFromSources(sources: OverrideSources): BackendOverride | null {
    if (!sources.isDev) return null;

    const url = sources.localBackendSwitcherEnabled
        ? readStorageOverride(sources.storage)
        : readEnvOverride(sources.env);
    if (!url) return null;

    // 裸 URL → 本地后端
    return { kind: 'local', url };
}

function isLocalBackendSwitcherEnabled(): boolean {
    return typeof __DRAFTING_LOCAL_BACKEND_SWITCHER_ENABLED__ === 'boolean'
        && __DRAFTING_LOCAL_BACKEND_SWITCHER_ENABLED__;
}

/** 读取并解析本地后端覆盖值；无覆盖 / 非 dev / 不可用时返回 null */
export function readOverride(): BackendOverride | null {
    return readOverrideFromSources({
        isDev: import.meta.env.DEV,
        localBackendSwitcherEnabled: isLocalBackendSwitcherEnabled(),
        storage: typeof localStorage === 'undefined' ? undefined : localStorage,
        env: import.meta.env,
    });
}

/**
 * 当前生效目标是否为「本地后端」。
 * 用于鉴权拦截器：本地后端需注入 X-User-* header，远程环境走 passport。
 *
 * 判断优先级：
 *  - 普通 pnpm dev：VITE_USE_LOCAL_BACKEND=1 时为 true
 *  - 本地后端切换器插件启用：存在 localStorage 本地覆盖时为 true
 *  - 生产构建 / 无覆盖：false，回退当前 devEnv 对应的远程后端
 */
export function isLocalBackendActive(): boolean {
    const override = readOverride();
    return override?.kind === 'local';
}
